import { Search, X } from "lucide-react";
import { useState, useEffect, useRef, ChangeEvent, KeyboardEvent } from "react";
import { CatalogItem, CatalogItemListReq, PropertyFilter } from '../types/catalog'; 
import { catalogClient } from '../services/grpc'; // Adjust path to your gRPC client
import * as pb from '../generated/catalog_pb';
import { Input } from "./ui/input";
import { ImageWithFallback } from "./figma/ImageWithFallback";
interface SearchResult {
  name: string;
  pic: string;
  url: string;
}

export const AutocompleteSearchBar = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);


  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Search effect
  useEffect(() => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }

    const fetchResults = async () => {
      setIsLoading(true);
      try {
        const req = new pb.SearchReq;
        req.setSearch(searchQuery);
        catalogClient.getSearchList(req, {}, (err, response) => {
          if (err) {
            console.error("Search error:", err);
            setResults([]);
          } else {
             const itemsList = response.getItemList();
            const mappedResults = itemsList.map(item => ({
              name: item.getName(),
              pic: item.getPic(),
              url: item.getUrl()
            }));
            setResults(mappedResults);
          }
          setIsLoading(false);
        });
      } catch (error) {
        console.error("Search failed:", error);
        setResults([]);
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [searchQuery]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setActiveIndex(-1);
    if (e.target.value.length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelect = (result: SearchResult) => {
    setSearchQuery(result.name);
    setShowSuggestions(false);
    window.location.href = result.url;
  };

  const handleClear = () => {
    setSearchQuery("");
    setResults([]);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;

    // Arrow down
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
    }
    // Arrow up
    else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : 0));
    }
    // Enter
    else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    }
    // Escape
    else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  return (
    <div className="flex-1 max-w-xl relative" ref={searchRef}>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />

        <Input
          type="text"
          placeholder="Поиск по товарам, категориям, брендам"
          className="p-10 pr-4 py-6 bg-white text-gray-900 border-0 rounded-lg text-base sm:py-3"
          value={searchQuery}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => searchQuery.length > 0 && setShowSuggestions(true)}
        />

        {isLoading && (
          <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin h-4 w-4 text-gray-400" />
          </div>
        )}

        {searchQuery && !isLoading && (
          <button
            onClick={handleClear}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {showSuggestions && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-96 overflow-auto">
          {results.map((result, index) => (
            <div
              key={result.pic}
              onClick={() => handleSelect(result)}
              className={`px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                index === activeIndex ? "bg-gray-50" : ""
              }`}
            >
              <div className="flex items-center">
                {result.pic && (
                   <ImageWithFallback
                                        src={`https://cdn.yutongoriginal.ru/product_images/${result.pic}.jpg`}
                                        alt={result.name}
                                        className="w-8 h-8 object-cover rounded mr-3"
                                      />

                )}
                <span className="text-gray-900">{result.name}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showSuggestions && results.length === 0 && searchQuery.length >= 2 && !isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 p-4 text-center text-gray-500">
          Ничего не найдено
        </div>
      )}
    </div>
  );
};